import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { FeedbackEvent } from '@bonusly/common-feedback';
import { GiveBoxEvent } from '@bonusly/common-give-box';
import { UserAvatar, UserPopover } from '@bonusly/common-user-popover';
import { Badge, Box, ChatAltSolid, ClappingHandsSolid, Flex, IconButton, rem, Text, Tooltip } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { ProfileLink } from '../../../../../../../modules/profile/components/profile-link';
import { useHomepageRedesignEnabled } from '../../BonusFeed/hooks/useHomepageRedesignEnabled';
import { useDaysSinceLastInteraction, useInteractionTooltip, useLastInteractedDate } from '../hooks/use-interaction';
import { Person } from '../types';

interface Props {
	person: Person;
}

export const PersonStats: FC<Props> = ({ person }) => {
	const { t } = useTranslation('tracker_widget');
	const dispatch = useDispatch();
	const { is_manager } = useCurrentUser();
	const { track } = useAnalytics();
	const canAccessAdvice = usePermission(Permission.CanAccessAdvice);
	const homepageRedesignEnabled = useHomepageRedesignEnabled();
	const showFeedbackFeatures = canAccessAdvice && !homepageRedesignEnabled;

	const user = {
		id: person.id,
		display_name: person.display_name,
		profile_pic_url: person.profile_pic_url,
		username: person.username,
	};

	const lastInteractionDate = useLastInteractedDate(person);
	const daysSinceInteraction = useDaysSinceLastInteraction(lastInteractionDate);
	const lastRecognizedText = useMemo(() => {
		if (!lastInteractionDate) {
			return t('person_list.never_interacted');
		} else if (daysSinceInteraction === 0) {
			return t('person_list.last_interacted_today');
		} else {
			return t('person_list.last_interacted', { count: daysSinceInteraction });
		}
	}, [daysSinceInteraction, lastInteractionDate, t]);

	const interactionCount = useMemo(() => {
		const count = showFeedbackFeatures ? person.recognition_count + person.feedback_given_count + person.feedback_requested_count : person.recognition_count;

		if (!count) return undefined;
		return count;
	}, [showFeedbackFeatures, person]);

	const interactionTooltipText = useInteractionTooltip(person);

	const recognize = () => {
		track(AnalyticsEvent.TrackerWidget.GiveRecognitionButtonClicked);
		dispatch(GiveBoxEvent.Open, {
			recipient: {
				id: person.id,
				displayName: person.display_name,
				profilePic: person.profile_pic_url,
				username: person.username,
			},
		});
	};

	const requestFeedback = () => {
		track(AnalyticsEvent.TrackerWidget.RequestFeedbackButtonClicked);
		dispatch(FeedbackEvent.OpenRequest, {
			defaultUsers: [
				{
					id: person.id,
					imageUrl: person.profile_pic_url,
					name: person.display_name,
					username: person.username,
				},
			],
		});
	};

	const giveFeedback = () => {
		track(AnalyticsEvent.TrackerWidget.GiveFeedbackButtonClicked);
		dispatch(FeedbackEvent.OpenUnsolicited, {
			defaultUsers: [
				{
					id: person.id,
					imageUrl: person.profile_pic_url,
					name: person.display_name,
					username: person.username,
				},
			],
		});
	};

	return (
		<Flex alignContent="flex-start" alignItems="center" flexDirection="row" gap={3} key={person.display_name}>
			<Box position="relative">
				<UserPopover userId={user.id}>
					<ProfileLink userId={person.id}>
						<Flex alignContent="flex-start" alignItems="center" flexDirection="row">
							<UserAvatar userId={user.id} />
						</Flex>
					</ProfileLink>
				</UserPopover>
				{interactionCount && (
					<Tooltip
						label={`${t('person_list.tooltip_title')} ${interactionTooltipText}`}
						hasArrow={true}
						placement="top"
						maxW="fit-content"
						maxWidth={rem(240)}
						overflow="visible"
						position="absolute"
					>
						<Badge
							bgColor="bg.subtle"
							borderColor="text.onColor"
							borderRadius="full"
							cursor="default"
							h={rem(18)}
							position="absolute"
							right="-5px"
							textAlign="center"
							top="-5px"
							w={rem(18)}
						>
							<Text color="text.soft" fontSize={1} fontWeight="semibold" w={rem(18)} position="absolute" right="0px">
								{interactionCount}
							</Text>
						</Badge>
					</Tooltip>
				)}
			</Box>
			<Flex alignContent="flex-start" alignItems="flex-start" flexDirection="column" w="100%">
				<Text fontSize={100} fontWeight="medium" color="text.default" lineHeight={6}>
					{person.display_name}
				</Text>
				<Text fontSize={50} fontWeight="normal" color="text.softest" lineHeight={5}>
					{lastRecognizedText}
				</Text>
			</Flex>
			<Tooltip hasArrow label={t('person_list.give_recognition')} openDelay={300} placement="top">
				<IconButton
					aria-label={t('person_list.give_recognition_aria_label', {
						name: person.display_name,
					})}
					backgroundColor="primary.bgAlpha"
					borderRadius="md"
					color="primary.fg"
					data-testid="give-recognition-button"
					icon={<ClappingHandsSolid h={5} w={5} />}
					ml="auto"
					onClick={recognize}
					size="sm"
					_hover={{
						backgroundColor: 'primary.bgAlphaHover',
					}}
				/>
			</Tooltip>

			{showFeedbackFeatures && (
				<Tooltip hasArrow label={is_manager ? t('person_list.give_feedback') : t('person_list.request_feedback')} openDelay={300} placement="top">
					<IconButton
						aria-label={
							is_manager
								? t('person_list.give_feedback_aria_label', {
										name: person.display_name,
									})
								: t('person_list.request_feedback_aria_label', {
										name: person.display_name,
									})
						}
						backgroundColor="hashtag.bgAlpha"
						borderRadius="md"
						color="hashtag.fgOnSurface"
						data-testid="feedback-button"
						icon={<ChatAltSolid h={5} w={5} />}
						onClick={is_manager ? giveFeedback : requestFeedback}
						p={1.5}
						size="sm"
						_hover={{
							backgroundColor: 'hashtag.bgAlphaHover',
						}}
					/>
				</Tooltip>
			)}
		</Flex>
	);
};
